import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './GuildWarCount.css';
import { API_BASE_URL } from '../config/api';

const NIHON_GID = 'd1431c50f9d963b8d66f195b156e7db8';

const translateLevel = (level) => {
  if (level >= 1 && level <= 99) return `${level}`;
  if (level >= 100 && level <= 198) return `M${level - 99}`;
  if (level >= 199 && level <= 297) return `S${level - 198}`;
  if (level >= 298 && level <= 396) return `U${level - 297}`;
  if (level >= 397 && level <= 495) return `L${level - 396}`;
  if (level >= 496) return `S'${level - 495}`;
  return '?';
};

const translatePvpLeaguePhase = (phase) => {
  const map = {
    LEGEND: 'L', MASTER_1: 'M1', MASTER_2: 'M2', MASTER_3: 'M3',
    DIAMOND_1: 'D1', DIAMOND_2: 'D2', DIAMOND_3: 'D3',
    GOLD_1: 'G1', GOLD_2: 'G2', GOLD_3: 'G3',
    SILVER_1: 'S1', SILVER_2: 'S2', SILVER_3: 'S3',
    BRONZE: 'B',
  };
  return map[phase] || phase;
};

const PVP_ORDER = ['L', 'M1', 'M2', 'M3', 'D1', 'D2', 'D3', 'G1', 'G2', 'G3', 'S1', 'S2', 'S3', 'B'];

const GuildInfoNihon = () => {
  const [defenders, setDefenders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'pvpLeaguePhase', direction: 'ascending' });

  // 編集フォーム
  const [selectedDefender, setSelectedDefender] = useState(null);
  const [lineName, setLineName] = useState('');
  const [selectedLineUser, setSelectedLineUser] = useState(null);
  const [updateMsg, setUpdateMsg] = useState('');

  // LINE メンバー一覧（フォーム内選択用）
  const [lineMembers, setLineMembers] = useState([]);
  const [isLoadingLine, setIsLoadingLine] = useState(false);

  const fetchGameMembers = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/guild-info-nihon/${NIHON_GID}/members`);
      setDefenders(res.data.map(m => ({
        ...m,
        pvpLeaguePhase: translatePvpLeaguePhase(m.pvpLeaguePhase),
      })));
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLineMembers = useCallback(async () => {
    setIsLoadingLine(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/line/group-members`);
      setLineMembers(res.data);
    } catch {
      // LINE メンバーが取得できなくても続行
    } finally {
      setIsLoadingLine(false);
    }
  }, []);

  useEffect(() => {
    fetchGameMembers();
    fetchLineMembers();
  }, [fetchGameMembers, fetchLineMembers]);

  // ---- ソート ----
  const sorted = [...defenders].sort((a, b) => {
    const { key, direction } = sortConfig;
    const order = direction === 'ascending' ? 1 : -1;
    if (key === 'level') return order * ((a.level || 0) - (b.level || 0));
    if (key === 'userName') return order * (a.userName || '').localeCompare(b.userName || '');
    if (key === 'lineName') return order * (a.lineName || '').localeCompare(b.lineName || '');
    if (key === 'pvpLeaguePhase') {
      const ia = PVP_ORDER.indexOf(a.pvpLeaguePhase || '');
      const ib = PVP_ORDER.indexOf(b.pvpLeaguePhase || '');
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return order * (ia - ib);
    }
    return 0;
  });

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const getSortIcon = (key) =>
    sortConfig.key === key ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : '⇅';

  // ---- 行クリック ----
  const handleRowClick = (defender) => {
    if (selectedDefender?.uid === defender.uid) {
      closeForm();
      return;
    }
    setSelectedDefender(defender);
    setLineName(defender.lineName || '');
    // 既に紐付け済みなら LINE メンバーを事前選択
    const linked = lineMembers.find(l => l.userId === defender.lineUserId);
    setSelectedLineUser(linked || null);
    setUpdateMsg('');
  };

  const closeForm = () => {
    setSelectedDefender(null);
    setLineName('');
    setSelectedLineUser(null);
  };

  // ---- LINE メンバー選択 ----
  const handleSelectLineUser = (member) => {
    if (selectedLineUser?.userId === member.userId) {
      setSelectedLineUser(null);
      setLineName(selectedDefender?.lineName || '');
    } else {
      setSelectedLineUser(member);
      setLineName(member.displayName);
    }
  };

  // ---- 更新 ----
  const handleUpdate = async () => {
    if (!selectedDefender) return;

    // 1. Optimistic UI update (即座に画面に反映)
    const targetUid = selectedDefender.uid;
    const targetLineUser = selectedLineUser;
    const targetLineName = lineName || (selectedLineUser ? selectedLineUser.displayName : '-');
    const targetLineUserId = selectedLineUser ? selectedLineUser.userId : null;
    const targetGroup = selectedDefender.group || '-';

    setDefenders((prev) =>
      prev.map((d) =>
        d.uid === targetUid
          ? { ...d, lineName: targetLineName, lineUserId: targetLineUserId }
          : d
      )
    );

    // フォームを閉じてすぐ次のユーザーを選択できるようにする
    closeForm();
    setUpdateMsg('✓ 通信中...');

    try {
      if (targetLineUser) {
        // LINE ユーザーと紐付け
        await axios.post(`${API_BASE_URL}/api/line/link-member`, {
          lineUserId: targetLineUserId,
          lineDisplayName: targetLineName,
          gameUid: targetUid,
          gameGroup: targetGroup,
          gid: NIHON_GID,
        });
      } else {
        // テキストのみ更新（LINE ID なし）
        await axios.put(`${API_BASE_URL}/api/guilds/update-line-group`, {
          uid: targetUid,
          lineName: targetLineName,
          group: targetGroup,
          gid: NIHON_GID,
        });
      }
      setUpdateMsg('✓ 更新しました');
      setTimeout(() => setUpdateMsg(''), 3000);
    } catch (err) {
      setUpdateMsg(`エラー: ${err.response?.data?.error || err.message}`);
    }
  };

  // ---- 紐付け解除 ----
  const handleUnlink = async () => {
    if (!selectedDefender) return;
    if (!window.confirm(`${selectedDefender.userName} の紐付けを解除しますか？`)) return;

    // Optimistic UI update
    const targetUid = selectedDefender.uid;
    setDefenders((prev) =>
      prev.map((d) =>
        d.uid === targetUid ? { ...d, lineName: '-', lineUserId: null } : d
      )
    );

    closeForm();
    setUpdateMsg('✓ 通信中...');

    try {
      await axios.delete(`${API_BASE_URL}/api/line/unlink-member`, {
        data: { gameUid: targetUid, gid: NIHON_GID },
      });
      setUpdateMsg('✓ 紐付けを解除しました');
      setTimeout(() => setUpdateMsg(''), 3000);
    } catch (err) {
      setUpdateMsg(`エラー: ${err.response?.data?.error || err.message}`);
    }
  };

  // lineUserId → LINE メンバー情報のマップ（アイコン表示用）
  const lineUserMap = Object.fromEntries(lineMembers.map(m => [m.userId, m]));

  // LINE メンバーのうち、他のゲームメンバーに紐付け済みのものを把握（グレーアウト用）
  const linkedLineUserIds = new Set(
    defenders.filter(d => d.lineUserId && d.uid !== selectedDefender?.uid).map(d => d.lineUserId)
  );

  return (
    <div className="guild-war-container">
      <div className="select-group">
        <h1>にほーん メンバー情報</h1>
      </div>

      {updateMsg && (
        <p style={{ color: updateMsg.startsWith('エラー') ? 'red' : '#4caf50', fontWeight: 'bold', margin: '8px 0' }}>
          {updateMsg}
        </p>
      )}

      {isError ? (
        <p>データ取得中にエラーが発生しました。</p>
      ) : isLoading ? (
        <p>Loading data...(It may take several minutes)</p>
      ) : (
        <>
          <table className="defender-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('level')}>Lv {getSortIcon('level')}</th>
                <th onClick={() => requestSort('userName')}>GameName {getSortIcon('userName')}</th>
                <th onClick={() => requestSort('lineName')}>LINEName {getSortIcon('lineName')}</th>
                <th onClick={() => requestSort('pvpLeaguePhase')}>PVP {getSortIcon('pvpLeaguePhase')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(defender => (
                <tr
                  key={defender.uid}
                  className="defender-row"
                  style={{ backgroundColor: selectedDefender?.uid === defender.uid ? 'rgba(74, 158, 255, 0.15)' : '' }}
                  onClick={() => handleRowClick(defender)}
                >
                  <td>{translateLevel(defender.level || 0)}</td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {defender.imageUrl && (
                        <img
                          src={`https://rangers.lerico.net/res/user_profile_img/${defender.imageUrl}.png`}
                          alt=""
                          style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      {defender.userName || 'unknown'}
                    </span>
                  </td>
                  <td>
                    {defender.lineUserId ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4caf50' }}>
                        {lineUserMap[defender.lineUserId]?.pictureUrl ? (
                          <img
                            src={lineUserMap[defender.lineUserId].pictureUrl}
                            alt=""
                            style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#2d5a2d', flexShrink: 0 }} />
                        )}
                        {defender.lineName || '-'}
                      </span>
                    ) : (
                      defender.lineName || '-'
                    )}
                  </td>
                  <td>{defender.pvpLeaguePhase || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 編集フォーム */}
          {selectedDefender && (
            <div className="update-form" style={{ marginTop: 16 }}>
              <h2 style={{ marginBottom: 12 }}>Updating: {selectedDefender.userName}</h2>

              {/* LINE メンバー選択リスト */}
              <div style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 6px' }}>
                  LINEメンバーから選択（または下のテキストで直接入力）
                </p>
                {isLoadingLine ? (
                  <p style={{ fontSize: 12, color: '#888' }}>LINE members loading...</p>
                ) : lineMembers.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#888' }}>
                    LINEメンバーが見つかりません（グループ内でメッセージが届くと自動で蓄積されます）
                  </p>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    maxHeight: 180,
                    overflowY: 'auto',
                    padding: 6,
                    border: '1px solid #444',
                    borderRadius: 4,
                  }}>
                    {lineMembers.map(member => {
                      const isSelected = selectedLineUser?.userId === member.userId;
                      const isLinkedElsewhere = linkedLineUserIds.has(member.userId) && selectedDefender?.lineUserId !== member.userId;
                      return (
                        <div
                          key={member.userId}
                          onClick={() => handleSelectLineUser(member)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: 20,
                            border: isSelected ? '2px solid #4a9eff' : isLinkedElsewhere ? '2px solid #bd9b3b' : '1px solid #ccc',
                            backgroundColor: isSelected ? 'rgba(74, 158, 255, 0.15)' : '#ffffff',
                            color: isSelected ? 'inherit' : '#333',
                            cursor: 'pointer',
                            opacity: 1,
                            fontSize: 13,
                          }}
                          title={isLinkedElsewhere ? 'すでに他のメンバーにも紐付けされています（サブ垢用等の再選択）' : ''}
                        >
                          {member.pictureUrl ? (
                            <img src={member.pictureUrl} alt="" style={{ width: 22, height: 22, borderRadius: '50%' }} />
                          ) : (
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>?</div>
                          )}
                          <span>{member.displayName}</span>
                          {isLinkedElsewhere && <span style={{ fontSize: 10, color: '#888' }}>（使用中）</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* テキスト入力 */}
              <input
                type="text"
                value={lineName}
                onChange={(e) => {
                  setLineName(e.target.value);
                  // 直接入力したら LINE ユーザーの選択を解除
                  if (selectedLineUser && e.target.value !== selectedLineUser.displayName) {
                    setSelectedLineUser(null);
                  }
                }}
                placeholder="LINE Name"
              />

              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={handleUpdate}>
                  {selectedLineUser ? 'Link & Update' : 'Update'}
                </button>
                {selectedDefender.lineUserId && (
                  <button onClick={handleUnlink} style={{ background: '#c00' }}>
                    Unlink
                  </button>
                )}
                <button onClick={closeForm} style={{ background: '#555' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GuildInfoNihon;
